/**
 * Appearance.
 *
 * The portal has always followed the operating system, which is the right
 * default and the wrong only option: people arrive on a machine set to dark,
 * see a portal that looks nothing like the one their colleague is describing,
 * and have no way to say otherwise.
 *
 * So: three choices, with "system" the default. The choice is a per-browser
 * preference rather than an account setting, because it is a property of the
 * screen you are sitting at, not of who you are — the same person wants dark
 * on the laptop at night and light on the desk under a window.
 *
 * The attribute this writes is read by `styles.css`; the inline script in
 * `index.html` writes the same attribute before first paint, so a forced theme
 * never flashes the other one on the way in. Both must agree on the key and
 * the attribute, which is why they are named here.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

/** Shared with the pre-paint script in `index.html`. Change both or neither. */
const STORAGE_KEY = 'relay-theme';

export type ThemeChoice = 'system' | 'light' | 'dark';

const CHOICES: readonly { readonly value: ThemeChoice; readonly label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

/**
 * What was chosen last, or "system".
 *
 * Wrapped because `localStorage` throws rather than returning null in a browser
 * with site data blocked, and an appearance preference is not worth a blank
 * portal.
 */
function storedChoice(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemeChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/**
 * "system" removes the attribute rather than writing a value, so the media
 * query in the stylesheet is back in charge. Anything else pins it.
 */
function apply(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.dataset['theme'] = choice;
}

export function ThemeToggle(): JSX.Element {
  const [choice, setChoice] = useState<ThemeChoice>(() => storedChoice());

  // The pre-paint script has already applied the stored choice, so this is for
  // the changes made afterwards rather than for the first render.
  useEffect(() => {
    apply(choice);
  }, [choice]);

  const pick = useCallback((next: ThemeChoice) => {
    setChoice(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A browser that will not store it still honours it for this tab, which
      // is better than refusing to switch at all.
    }
  }, []);

  return (
    <div className="theme-toggle" role="group" aria-label="Appearance">
      {CHOICES.map((option) => (
        <button
          key={option.value}
          type="button"
          className="theme-choice"
          aria-pressed={choice === option.value}
          onClick={() => pick(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
